import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

interface JarRenderProps {
    className?: string
    // Draws the white lid resting behind the jar.
    lid?: boolean
    label?: string
}

const buildSwirl = (cx: number, cy: number): string => {
    const points: string[] = []
    const turns = 3.4
    const steps = 150

    for (let index = 0; index <= steps; index++) {
        const theta = (index / steps) * turns * Math.PI * 2
        const radius = 5 + 21 * (theta / (Math.PI * 2)) + 2.2 * Math.sin(theta * 3)
        const x = cx + radius * Math.cos(theta)
        const y = cy + radius * Math.sin(theta) * 0.96

        points.push(`${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`)
    }

    return points.join(' ')
}

const SWIRL_CENTERED = buildSwirl(170, 170)
const SWIRL_WITH_LID = buildSwirl(150, 186)

const JarRender: FC<JarRenderProps> = ({ className, lid = false, label }): ReactNode => {
    const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
    const id = (name: string): string => `jar-${uid}-${name}`
    const url = (name: string): string => `url(#${id(name)})`

    const cx = lid ? 150 : 170
    const cy = lid ? 186 : 170
    const swirl = lid ? SWIRL_WITH_LID : SWIRL_CENTERED

    return (
        <svg
            viewBox='0 0 340 340'
            className={cn('block size-full overflow-visible', className)}
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            focusable='false'
        >
            <defs>
                <radialGradient id={id('jar')} cx='0.36' cy='0.32' r='0.78'>
                    <stop offset='0' stopColor='#6488FF' />
                    <stop offset='0.42' stopColor='#2B55F5' />
                    <stop offset='0.82' stopColor='#1E3DC6' />
                    <stop offset='1' stopColor='#162E9E' />
                </radialGradient>

                <radialGradient id={id('wall')} cx='0.5' cy='0.5' r='0.5'>
                    <stop offset='0.8' stopColor='#2147DB' />
                    <stop offset='0.94' stopColor='#142C8E' />
                    <stop offset='1' stopColor='#0E2170' />
                </radialGradient>

                <radialGradient id={id('balm')} cx='0.4' cy='0.36' r='0.68'>
                    <stop offset='0' stopColor='#FFFFFF' />
                    <stop offset='0.5' stopColor='#FCF7F2' />
                    <stop offset='0.84' stopColor='#F0E5DB' />
                    <stop offset='1' stopColor='#DFCFC1' />
                </radialGradient>

                <radialGradient id={id('lid')} cx='0.38' cy='0.34' r='0.75'>
                    <stop offset='0' stopColor='#FFFFFF' />
                    <stop offset='0.7' stopColor='#F4EFEB' />
                    <stop offset='1' stopColor='#D9CFC8' />
                </radialGradient>

                <filter id={id('soft')} x='-30%' y='-30%' width='160%' height='160%'>
                    <feGaussianBlur stdDeviation='9' />
                </filter>

                <filter id={id('fine')} x='-20%' y='-20%' width='140%' height='140%'>
                    <feGaussianBlur stdDeviation='1.6' />
                </filter>

                <clipPath id={id('balmClip')}>
                    <circle cx={cx} cy={cy} r='100' />
                </clipPath>
            </defs>

            {lid && (
                <g>
                    <ellipse cx='272' cy='88' rx='60' ry='58' fill='#4A2B20' fillOpacity='0.16' filter={url('soft')} />
                    <circle cx='262' cy='80' r='60' fill={url('lid')} />
                    <circle cx='262' cy='80' r='51' fill='none' stroke='#4A2B20' strokeOpacity='0.08' strokeWidth='1.5' />
                    <circle cx='262' cy='80' r='59' fill='none' stroke='#FFFFFF' strokeOpacity='0.9' strokeWidth='1.5' />
                    <path d='M228 56 A44 44 0 0 1 270 38' fill='none' stroke='#FFFFFF' strokeWidth='5' strokeLinecap='round' filter={url('fine')} />
                </g>
            )}

            <ellipse cx={cx + 10} cy={cy + 16} rx='126' ry='122' fill='#4A2B20' fillOpacity='0.2' filter={url('soft')} />
            <circle cx={cx} cy={cy} r='124' fill={url('jar')} />
            <circle cx={cx} cy={cy} r='123' fill='none' stroke='#FFFFFF' strokeOpacity='0.22' strokeWidth='1.5' />
            <circle cx={cx} cy={cy} r='110' fill={url('wall')} />
            <circle cx={cx} cy={cy} r='100' fill={url('balm')} />

            <g clipPath={url('balmClip')}>
                <path d={swirl} fill='none' stroke='#E4D6CA' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' />
                <path d={swirl} fill='none' stroke='#FFFFFF' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' transform='translate(-1.6 -1.8)' />
                <circle cx={cx} cy={cy} r='54' fill='#FFFFFF' fillOpacity='0.28' filter={url('soft')} />
                <circle cx={cx - 8} cy={cy - 9} r='9' fill='#FFFFFF' filter={url('fine')} />
            </g>

            <path
                d={`M${cx - 110} ${cy - 40} A117 117 0 0 1 ${cx - 40} ${cy - 110}`}
                fill='none'
                stroke='#FFFFFF'
                strokeOpacity='0.55'
                strokeWidth='4'
                strokeLinecap='round'
                filter={url('fine')}
            />
        </svg>
    )
}

export default JarRender
